import { z } from 'zod';
import { inquiryAddressSchema } from '../inquiry/address.js';
import { personEmailSchema } from '../people/person.js';
import { uuidV7Schema } from '../uuid.js';
/**
 * L'**avis de passage** — le constat qu'un agent s'est déplacé sans pouvoir contrôler.
 *
 * ⚠️ **Ce n'est pas une relance**, et c'est la distinction que ce fichier tient. Une relance dit
 * « le délai réglementaire court » et se **calcule** (`deadline.ts`, dans ce même domaine) ; un
 * avis de passage dit « nous sommes venus le 28 à 08:34, personne n'a ouvert », et se
 * **constate** — d'où une ligne en base, horodatée par la tablette.
 *
 * ⚠️ **Sa suite est celle du client** (M40, #421) : Relance, RDV1, RDV2 à Nantes Métropole,
 * Relance puis Recommandé à Mauges Communauté. Chaque étape se fait **par e-mail ou par
 * courrier**, et l'app retient lequel et quand. La décision du 21/09 (« juste un e-mail ») est
 * dépassée ; l'e-mail d'avis, lui, reste (`apps/api/src/inquiries/passage-notice-email.ts`).
 *
 * La chaîne, telle que les deux prototypes la dessinent :
 *
 * 1. **tablette** — l'agent devant une porte close déclenche « Avis de passage », horodaté
 *    (`mocks/ghp-ct/app.js:343-352`, bouton `data-avis-passage`) ;
 * 2. **bureau** — la liste « Avis de passage » les reçoit, et c'est d'elle qu'on écrit à l'usager
 *    (`mocks/ghp-bo/ext.js:47-100`) ;
 * 3. l'avis se **clôt** quand l'usager s'est manifesté, ou quand la carence est actée.
 */
/**
 * Pourquoi le contrôle n'a pas pu se faire.
 *
 * ⚠️ **Le motif dit le fait constaté, il ne décide de rien.** Il choisissait le courrier tant
 * qu'il y en avait deux ; il n'y a plus qu'un envoi, et c'est le motif qui en écrit la phrase
 * centrale — « personne n'était présent » n'est pas « l'accès a été refusé », et l'usager doit
 * pouvoir reconnaître sa propre journée dans ce qu'il lit.
 *
 * Verbatim du prototype (`mocks/ghp-bo/ext.js:19-24`) pour les libellés français ; les codes
 * sont en anglais comme tout identifiant.
 */
export const PASSAGE_NOTICE_REASONS = [
    'reminder_left',
    'absent',
    'access_refused',
    'second_visit_no_answer',
];
export const passageNoticeReasonSchema = z.enum(PASSAGE_NOTICE_REASONS);
/** Les libellés du prototype, mot pour mot — ce que le gestionnaire lit dans sa colonne. */
export const PASSAGE_NOTICE_REASON_LABELS = {
    /** Une relance déposée dans la boîte aux lettres, sur un systématique sans rendez-vous (M40). */
    reminder_left: 'Relance déposée',
    /** Le rendez-vous était pris, personne n'était là (M40). */
    absent: 'Absent au rendez-vous',
    access_refused: 'Accès refusé',
    second_visit_no_answer: '2ᵉ passage sans réponse',
};
/**
 * Où en est l'avis.
 *
 * ⚠️ **L'envoi de l'e-mail n'est pas un état, c'est un événement daté** (`emailQueuedAt`) — même
 * parti que M26 pour l'export client, et pour la même raison : une même ligne peut recevoir un
 * second envoi, et un état « avisé » qu'il faudrait tenir cohérent avec la date finirait par la
 * contredire. La pastille « Avis envoyé » du prototype se lit sur la date.
 *
 * ⚠️ **`reminding` n'a pour l'instant aucun geste qui l'écrive**, et il est déclaré quand même :
 * le prototype l'affiche (« Relance en cours »), et un état absent de l'énumération ferait jeter
 * la ligne entière par le schéma au lieu de l'afficher. Il entrera quand la relance téléphonique
 * aura son geste.
 */
export const PASSAGE_NOTICE_STATUSES = ['new', 'reminding', 'settled'];
export const passageNoticeStatusSchema = z.enum(PASSAGE_NOTICE_STATUSES);
/** Verbatim du prototype (`AVIS_STATUT`), moins l'état qui nommait un courrier. */
/**
 * Les états d'un avis **encore en cours** — tout sauf « traité ».
 *
 * ⚠️ **Écrit une fois, parce que deux écrans en dépendent.** La pastille de la liste « Avis de
 * passage » compte ce qui attend un geste, et la colonne « État » du tableau des demandes montre
 * « Avis de passage » sur les mêmes lignes. Deux définitions de « en cours » feraient afficher
 * l'avis sur une demande que le bureau vient de solder — et un spec refuse qu'un état de plus
 * entre dans l'énumération sans qu'on ait dit de quel côté il tombe.
 *
 * ⚠️ **L'envoi de l'e-mail ne clôt rien.** Il est une date (`emailQueuedAt`), pas un état : on
 * attend toujours que l'usager se manifeste. Seul « marquer traité » ferme l'avis.
 */
export const PASSAGE_NOTICE_OPEN_STATUSES = PASSAGE_NOTICE_STATUSES.filter((status) => status !== 'settled');
export const PASSAGE_NOTICE_STATUS_LABELS = {
    new: 'Nouveau',
    reminding: 'Relance en cours',
    settled: 'Traité',
};
/**
 * Les étapes d'une suite (M40) — celles des clients de GHP. Un client range les siennes dans
 * l'ordre (`Client.passage_follow_up`).
 */
export const FOLLOW_UP_STEPS = [
    'reminder',
    'appointment_1',
    'appointment_2',
    'registered_letter',
];
export const followUpStepSchema = z.enum(FOLLOW_UP_STEPS);
export const FOLLOW_UP_STEP_LABELS = {
    reminder: 'Relance',
    appointment_1: 'RDV1',
    appointment_2: 'RDV2',
    registered_letter: 'Recommandé',
};
/**
 * Les étapes qui **imposent un rendez-vous** (#574) : le courrier ou l'e-mail du RDV1, puis du RDV2,
 * annonce une date. Ce rendez-vous est un vrai rendez-vous — au planning, dans l'agenda, sur la
 * pré-fiche —, marqué de l'étape qui l'impose (`Appointment.imposed_step`).
 */
export const IMPOSED_APPOINTMENT_STEPS = ['appointment_1', 'appointment_2'];
export function isImposedAppointmentStep(step) {
    return IMPOSED_APPOINTMENT_STEPS.includes(step);
}
/** Comment une étape est partie : l'app retient lequel, et quand (M40). */
export const FOLLOW_UP_CHANNELS = ['email', 'letter'];
export const followUpChannelSchema = z.enum(FOLLOW_UP_CHANNELS);
export const FOLLOW_UP_CHANNEL_LABELS = {
    email: 'E-mail',
    letter: 'Courrier',
};
/** Une étape faite : laquelle, par quel moyen, quel jour. */
export const followUpStepDoneSchema = z.object({
    step: followUpStepSchema,
    channel: followUpChannelSchema,
    doneAt: z.iso.date(),
});
/** « Étape faite » : le moyen et le jour. L'étape est la suivante de la suite, le serveur la sait. */
export const recordFollowUpStepSchema = z.strictObject({
    channel: followUpChannelSchema,
    doneAt: z.iso.date(),
    /**
     * La date et l'heure du rendez-vous imposé, sur un RDV1 ou un RDV2 (#574) — le bureau la choisit
     * en préparant son courrier. Le serveur l'exige sur ces deux étapes, et elles seules : c'est lui
     * qui sait quelle étape vient.
     */
    scheduledAt: z.iso.datetime({ offset: true }).optional(),
});
/** La première étape de la suite qui n'est pas faite, ou `null` : la suite est finie, ou vide. */
export function nextFollowUpStep(followUp, done) {
    const doneSteps = new Set(done.map((step) => step.step));
    return followUp.find((step) => !doneSteps.has(step)) ?? null;
}
/**
 * L'avis tel qu'il vit en base et traverse le réseau.
 *
 * ⚠️ `noticedAt` est **obligatoire** : l'horodatage **est** le constat. C'est lui que l'e-mail
 * cite, et un avis sans date ne prouverait aucun déplacement.
 */
export const passageNoticeSchema = z.object({
    id: uuidV7Schema,
    inquiryId: uuidV7Schema,
    /** L'instant du passage, fuseau compris (M5) — relevé par la tablette, pas par le serveur. */
    noticedAt: z.iso.datetime({ offset: true }),
    reason: passageNoticeReasonSchema,
    status: passageNoticeStatusSchema,
    /**
     * Quand l'e-mail d'avis est parti **en file**, ou `null` si aucun ne l'a encore été.
     *
     * ⚠️ **`queued` et non `sent`, et la nuance est le contrat** — la même que porte
     * `MailerService.enqueue` : la ligne retient que GHP a lâché l'envoi, pas que le fournisseur
     * l'a remis. Un écran qui promettrait « reçu » mentirait à un gestionnaire qui compte dessus.
     */
    emailQueuedAt: z.iso.datetime({ offset: true }).nullable(),
});
/**
 * Ce que la **tablette** dépose quand l'agent constate un passage sans contrôle.
 *
 * ⚠️ **Une clé d'idempotence obligatoire, et métier** — même règle que le dépôt d'un relevé
 * (D4) : un agent hors réseau qui appuie deux fois, ou une file d'envoi qui rejoue, ne doit pas
 * produire deux avis pour le même passage — donc deux e-mails à la même personne, qui appellera
 * pour comprendre.
 *
 * ⚠️ **`noticedAt` vient de la tablette, pas du serveur.** L'agent est parfois hors réseau au
 * moment du passage et son envoi part plus tard : dater à l'arrivée citerait dans l'e-mail une
 * heure à laquelle personne n'est passé.
 */
export const recordPassageNoticeSchema = z.object({
    inquiryId: uuidV7Schema,
    noticedAt: z.iso.datetime({ offset: true }),
    reason: passageNoticeReasonSchema,
    idempotencyKey: z.string().trim().min(1).max(200),
});
/**
 * Une ligne de la liste « Avis de passage » du bureau — l'avis **et** de quoi l'envoyer sans
 * ouvrir la demande.
 *
 * ⚠️ **Tout ce qui vient de la demande est nullable**, et pour les raisons habituelles : la Réf.
 * GHP est facultative (M1), une demande importée n'a pas toujours de demandeur (M24), et le
 * client vient d'une chaîne `Site → Lot → Client` que toutes ne portent pas.
 */
export const passageNoticeRowSchema = passageNoticeSchema.extend({
    /** La case « Urgent » du bureau (Sentry BACK-OFFICE-X), dite en rouge sur la ligne. */
    urgent: z.boolean().default(false),
    reference: z.string().nullable(),
    caseReference: z.string().nullable(),
    address: inquiryAddressSchema,
    /** Le destinataire de l'e-mail — le demandeur de la demande, quand elle en a un. */
    recipient: z.string().nullable(),
    /**
     * Son adresse e-mail, **ou `null` quand il n'y en a pas d'utilisable**.
     *
     * ⚠️ **Elle est sur la ligne pour que l'écran le dise avant le clic** (M21). Sans elle, le
     * bouton « Envoyer » s'offre sur une ligne dont l'envoi ne peut pas partir, et le gestionnaire
     * repart en croyant l'usager averti. `person.email` est un `text()` nu alimenté par des
     * imports client (M15) : « tel uniquement » est une valeur qu'on y trouve, et elle vaut `null`
     * ici.
     */
    recipientEmail: personEmailSchema.nullable(),
    clientName: z.string().nullable(),
    /**
     * L'équipe de la demande, ou `null` quand aucune ne la porte (#669).
     *
     * C'est son agenda que « Étape faite » relit pour avertir quand le RDV1 ou le RDV2 imposé tombe
     * sur un créneau déjà pris. Sans équipe, il n'y a aucun agenda à relire.
     */
    team: z
        .object({
        id: uuidV7Schema,
        name: z.string(),
    })
        .nullable(),
    /** La suite du client de la demande (M40), dans l'ordre ; vide pour un client qui n'en a pas. */
    followUp: z.array(followUpStepSchema).default([]),
    /** Les étapes faites, dans l'ordre où elles l'ont été. */
    stepsDone: z.array(followUpStepDoneSchema).default([]),
});
export const passageNoticePageSchema = z.object({
    items: z.array(passageNoticeRowSchema),
    total: z.number().int().min(0),
    /** Combien attendent encore un geste — la pastille du bandeau du prototype. */
    pending: z.number().int().min(0),
});
/**
 * Ce que « Étape faite » rend : la ligne à jour, **et si la confirmation du rendez-vous imposé est
 * partie** (#574).
 *
 * ⚠️ `confirmationQueued` vaut `null` quand aucune n'a été tentée — une étape sans rendez-vous, ou
 * un RDV parti par courrier —, et `false` quand l'e-mail n'a pas pu partir : personne de joignable.
 * C'est `false` que l'écran doit dire (M21), comme pour une pose de rendez-vous.
 */
export const recordedFollowUpStepSchema = z.object({
    notice: passageNoticeRowSchema,
    confirmationQueued: z.boolean().nullable(),
});
/**
 * Ce que l'envoi rend : l'avis remis à jour, **et si l'e-mail est parti**.
 *
 * ⚠️ **`emailQueued: false` n'est pas une erreur**, et l'interface doit le montrer plutôt que de
 * le taire — même enveloppe et même raison que `scheduledAppointmentSchema` (M21, M24) : le
 * demandeur manque, son adresse est inexploitable, ou l'envoi était déjà en file. Un
 * gestionnaire qui croit l'usager averti alors qu'il ne l'est pas ne le rappellera jamais.
 *
 * ⚠️ **L'avis revient dans la réponse** pour que l'écran réaligne sa ligne sans relire la liste :
 * `emailQueuedAt` vient d'être posé, et c'est lui qui porte la pastille.
 */
export const notifiedPassageNoticeSchema = z.object({
    notice: passageNoticeSchema,
    emailQueued: z.boolean(),
});
//# sourceMappingURL=passage-notice.js.map